<template>
    <div class="animated fadeIn">
        <!-- CARD 1: PENCARIAN -->
        <div class="section-card mb-4">
            <div class="section-card-header">
                <span class="section-card-title">
                    {{ pageTitle }}
                </span>
                <span class="text-muted small">Langkah 1 — cari data anggota atau faktur.</span>
            </div>
            <div class="section-card-body">
                <!-- Kriteria Pills -->
                <div class="form-group mb-3">
                    <label class="d-block font-weight-bold mb-2">Cari berdasarkan</label>
                    <div class="search-criteria-pills">
                        <button type="button" 
                            class="criteria-pill-btn" 
                            :class="{ active: searchAnggotaCriteria === 'nama' }"
                            @click="setSearchAnggotaCriteria('nama')">
                            Nama Anggota
                        </button>
                        <button type="button" 
                            class="criteria-pill-btn" 
                            :class="{ active: searchAnggotaCriteria === 'faktur' }"
                            @click="setSearchAnggotaCriteria('faktur')">
                            No. Faktur
                        </button>
                        <button type="button" 
                            class="criteria-pill-btn" 
                            :class="{ active: searchAnggotaCriteria === 'nik' }"
                            @click="setSearchAnggotaCriteria('nik')">
                            No. NIK
                        </button>
                    </div>
                </div>

                <!-- Form Input & Buttons -->
                <div class="row align-items-end">
                    <div class="col-md-5 col-sm-12">
                        <div class="form-group mb-0">
                            <label class="font-weight-bold mb-1">{{ searchCriteriaLabel }}</label>
                            <b-form-input 
                                ref="inputSearchAnggota"
                                v-model="searchAnggotaQuery" 
                                :placeholder="searchCriteriaPlaceholder" 
                                v-on:keyup.enter="handleSearchAnggota"
                                :disabled="isSearchingAnggota"
                            ></b-form-input>
                        </div>
                    </div>
                    <div class="col-md-7 col-sm-12 mt-3 mt-md-0 d-flex gap-2">
                        <b-button class="btn-pill-primary mr-2" :disabled="isSearchingAnggota" @click="handleSearchAnggota">
                            <b-spinner small class="mr-1" v-if="isSearchingAnggota"></b-spinner>
                            <i class="fa fa-search mr-1" v-else></i> Cari
                        </b-button>
                        <b-button class="btn-outline-neutral" :disabled="isSearchingAnggota" @click="handleResetAnggotaSearch">
                            Reset
                        </b-button>
                    </div>
                </div>

                <!-- Multi Anggota Results Table (if > 1 match) -->
                <div v-if="showAnggotaResultList" class="mt-4 pt-3 border-top">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <strong class="small text-muted">Ditemukan beberapa anggota. Silakan pilih:</strong>
                        <span class="badge badge-secondary">{{ searchAnggotaResults.length }} data</span>
                    </div>
                    <b-table bordered hover small responsive :items="searchAnggotaResults" :fields="anggotaResultFields">
                        <template v-slot:cell(action)="row">
                            <b-button size="sm" class="btn-pill-primary" @click="selectAnggotaDirect(row.item)">
                                Pilih
                            </b-button>
                        </template>
                    </b-table>
                </div>
            </div>
        </div>

        <!-- CARD 2: DETAIL ANGGOTA -->
        <div class="section-card mb-4" v-if="showDetailAnggota && anggota.id">
            <div class="section-card-header">
                <span class="section-card-title">DETAIL ANGGOTA</span>
                <div class="section-card-actions">
                    <b-button class="btn-outline-neutral" @click="$bvModal.show('modal-detail-anggota')">
                        Detail Lengkap
                    </b-button>
                </div>
            </div>
            <div class="section-card-body">
                <div class="member-info-panel">
                    <b-row>
                        <b-col md="6">
                            <div class="member-item-row">
                                <span class="member-item-label">No. Anggota</span>
                                <span class="member-item-value font-weight-bold text-dark">{{ anggota.no }}</span>
                            </div>
                            <div class="member-item-row">
                                <span class="member-item-label">No. Identitas</span>
                                <span class="member-item-value">{{ anggota.noIdentitas }}</span>
                            </div>
                            <div class="member-item-row">
                                <span class="member-item-label">No. HP</span>
                                <span class="member-item-value">{{ anggota.noHp }}</span>
                            </div>
                            <div class="member-item-row">
                                <span class="member-item-label">Alamat</span>
                                <span class="member-item-value">{{ anggota.alamat }}</span>
                            </div>
                        </b-col>
                        <b-col md="6">
                            <div class="member-item-row">
                                <span class="member-item-label">Jenis Identitas</span>
                                <span class="member-item-value">{{ anggota.jenisIdentitas }}</span>
                            </div>
                            <div class="member-item-row">
                                <span class="member-item-label">Nama</span>
                                <span class="member-item-value">{{ anggota.nama }}</span>
                            </div>
                            <div class="member-item-row">
                                <span class="member-item-label">No. Rumah</span>
                                <span class="member-item-value">{{ anggota.noRumah || '-' }}</span>
                            </div>
                        </b-col>
                    </b-row>
                </div>
            </div>
        </div>

        <!-- CARD 3: DAFTAR GADAI ANGGOTA / DAFTAR GADAI -->
        <div class="section-card mb-4">
            <div class="section-card-header">
                <div>
                    <h5 class="section-card-title mb-0 font-weight-bold text-dark" style="font-size: 16px;">
                        {{ showDetailAnggota ? 'Daftar Gadai Anggota' : 'Daftar Gadai' }}
                    </h5>
                    <div class="text-muted small mt-1">
                        {{ showDetailAnggota ? 'Langkah 2 — pilih faktur untuk melanjutkan pembayaran.' : 'Pilih faktur untuk melanjutkan pembayaran.' }}
                    </div>
                </div>
                <div class="metric-header-summary">
                    <div class="metric-summary-item">
                        <span class="metric-summary-label">Gadai berjalan</span>
                        <span class="metric-summary-value">{{ totalGadaiBerjalan }} faktur</span>
                    </div>
                    <div class="metric-summary-item">
                        <span class="metric-summary-label">Total kewajiban</span>
                        <span class="metric-summary-value">Rp. {{ totalKewajibanBerjalan }}</span>
                    </div>
                </div>
            </div>
            <div class="section-card-body">
                <b-table bordered hover show-empty
                    :busy="isBusy"
                    :items="daftarGadai" 
                    :fields="fields" 
                    responsive class="mb-3">
                    <template v-slot:table-busy>
                        <div class="text-center text-danger my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>  Loading data...</strong>
                        </div>
                    </template>
                    <template v-slot:empty>
                        Tidak ada data gadai.
                    </template>
                    <template v-slot:cell(noFaktur)="row">
                        <div>
                            <span class="font-weight-bold">{{ row.item.noFaktur }}</span>
                            <div v-if="jenisPembayaran == 'PP' && row.item.counterPerpanjangan > 0" class="mt-1">
                                <b-badge :variant="row.item.counterPerpanjangan >= (row.item.maxPerpanjangan ? row.item.maxPerpanjangan - 1 : 3) ? 'warning' : 'info'" style="font-size: 11px;">
                                    {{ row.item.counterPerpanjangan >= (row.item.maxPerpanjangan ? row.item.maxPerpanjangan - 1 : 3) ? 'Perpanjangan ke-' + row.item.counterPerpanjangan + ' dari ' + (row.item.maxPerpanjangan || 4) + ' (Menuju Rollover)' : 'Perpanjangan ke-' + row.item.counterPerpanjangan + ' dari ' + (row.item.maxPerpanjangan || 4) }}
                                </b-badge>
                            </div>
                            <div v-if="row.item.parentNoFaktur" class="mt-1">
                                <span class="badge badge-light border text-muted" style="font-size: 11px;">
                                    <i class="fa fa-link mr-1"></i> Dari: {{ row.item.parentNoFaktur }}
                                </span>
                            </div>
                        </div>
                    </template>
                    <template v-slot:cell(status)="row">
                        <b-badge variant="dark" v-if="row.item.status && row.item.status.name == 'Baru'">BARU</b-badge>
                        <b-badge variant="primary" v-else-if="row.item.status && row.item.status.name == 'Berjalan'">BERJALAN</b-badge>
                        <b-badge variant="danger" v-else-if="row.item.status && row.item.status.name == 'Void'">VOID</b-badge>
                        <b-badge variant="success" v-else-if="row.item.status && row.item.status.name == 'Lunas'">LUNAS</b-badge>
                        <b-badge v-else>{{ row.item.status ? row.item.status.name : '-' }}</b-badge>     
                    </template>
                    <template v-slot:head(status)>
                        Status 
                        <i class="fa fa-question tooltip-trigger-btn ml-1" @click="$bvModal.show('modal-info-status')"/>
                    </template>
                    <template v-slot:cell(nilaiPinjamanEfektif)="row">
                        <div class="text-right no-wrap font-weight-bold">Rp. {{ $helper.addThousandSeparator(row.item.nilaiPinjamanAwal) }}</div>
                        <div class="text-right text-muted small">Sisa Rp. {{ $helper.addThousandSeparator(row.item.nilaiPinjamanEfektif) }}</div>
                    </template>
                    <template v-slot:cell(barang)="row">
                        <div>
                            <p class="mb-0 font-weight-bold">{{row.item.jenisBarang}}</p>
                            <p class="mb-0 smallText text-muted">{{row.item.merk}} - {{row.item.tipe}}</p>
                        </div>
                    </template>
                    <template v-slot:cell(action)="row">
                        <div class="no-wrap">
                            <b-button size="sm" class="btn-pill-primary" name="bayarBtn" @click="checkPembayaran(row.item.id, row.item.noFaktur)">
                                Lihat Faktur
                            </b-button>
                        </div>
                    </template>
                </b-table>

                <div class="pagination-wrapper d-flex align-items-center justify-content-between">
                    <b-input-group prepend="Tampilkan" class="align-items-center tableShowItem">
                        <b-form-select v-model="input.show" id="showItems" @change="onLimitChange" :options="options">
                            <template v-slot:first>
                                <b-form-select-option :value="null" disabled />
                            </template>
                        </b-form-select>
                        <span class="ml-2">{{ totalViewText }}</span>
                    </b-input-group>
                    <b-pagination class="mb-0"
                        v-model="currentPage"
                        :total-rows="rows"
                        :per-page="perPage"
                        aria-controls="my-table"
                        @input="onPageChange"
                    ></b-pagination>
                </div>
            </div>
        </div>

        <!-- MODAL DETAIL ANGGOTA -->
        <b-modal id="modal-detail-anggota" body-class="modal-nopadding" size="xl" title="Detail Anggota" no-close-on-backdrop>
            <DetailAnggota v-if="anggota.id" :isModal="true" :idAnggota="anggota.id" />
            <template v-slot:modal-footer="{ cancel }">
                <b-button variant="secondary" name="cancelDetailAnggotaBtn" @click="cancel()">
                    Tutup
                </b-button>
            </template>
        </b-modal>

        <!-- MODAL INFO STATUS -->
        <b-modal id="modal-info-status" title="Info Status" no-close-on-backdrop>
            <div class="detail-data-wrapper mb-3">
                <h6 class="form-title mb-1">BARU</h6>
                <p class="smallText mb-0">Transaksi gadai yang baru diinput dan belum ditransaksikan. Seluruh gadai BARU yang tidak dieksekusi, akan dihapus keesokan harinya.</p>
            </div>
            <div class="detail-data-wrapper mb-3">
                <h6 class="form-title mb-1">BERJALAN</h6>
                <p class="smallText mb-0">Transaksi gadai aktif yang sudah dieksekusi dan dicetak SBGnya.</p>
            </div>
            <div class="detail-data-wrapper mb-3">
                <h6 class="form-title mb-1">VOID</h6>
                <p class="smallText mb-0">Transaksi gadai sudah digugurkan (VOID).</p>
            </div>
            <div class="detail-data-wrapper mb-0">
                <h6 class="form-title mb-1">LUNAS</h6>
                <p class="smallText mb-0">Transaksi gadai sudah lunas.</p>
            </div>
            <template v-slot:modal-footer="{ cancel }">
                <b-button variant="secondary" name="closeDetailBtn" @click="cancel()">
                    Tutup
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import DetailAnggota from '../../views/master/anggota/DetailAnggota'

export default {
    name: 'pembayaran-gadai',
    components: {
        DetailAnggota,
    },
    props: {
        jenisPembayaran: { type: String, required: true },
    },
    data(){
        return {
            isBusy: true,
            currentSearchMode: 'faktur', // 'faktur' | 'anggota'
            showDetailAnggota: false,
            input: {
                show: 10,
            },
            anggota: {
                id: null,
                nama: "-",
                no: "-",
                jenisBarang: "-",
                jenisIdentitas: "-",
                noIdentitas: "-",
                noHp: "-",
                noRumah: "-",
                alamat: "-",
                isComplete: false
            },
            fields: [
                { key: "noFaktur", label: "No. Faktur" },
                { key: "namaAnggota", label: "Nama Anggota" },
                { key: "barang", label: "Barang" },
                { key: "nilaiPinjamanEfektif", label: "Total Pinjaman" },
                { key: "status", label: "Status" },
                { key: "action", label: "Aksi" },
            ],
            daftarGadai: [],
            // Search Anggota Inline State
            searchAnggotaCriteria: 'nama', // 'nama' | 'faktur' | 'nik'
            searchAnggotaQuery: '',
            isSearchingAnggota: false,
            showAnggotaResultList: false,
            searchAnggotaResults: [],
            anggotaResultFields: [
                { key: 'no', label: 'No. Anggota' },
                { key: 'nama', label: 'Nama' },
                { key: 'noIdentitas', label: 'No. KTP/NIK' },
                { key: 'noHp', label: 'No. HP' },
                { key: 'action', label: 'Aksi' }
            ],
            options: [
                { value: 10, text: '10'},
                { value: 20, text: '20'},
                { value: 30, text: '30'},
                { value: 40, text: '40'},
                { value: 50, text: '50'},
            ],
            currentPage: 1,
            perPage: 10,
            rows: null,
            totalViewText: "",
            page: 0,
            size: 10,
            loading: false,
        }
    },
    computed: {
        pageTitle() {
            return this.jenisPembayaran === 'PL' ? 'Pelunasan Gadai' : 'Perpanjangan Gadai'
        },
        searchCriteriaLabel() {
            if (this.searchAnggotaCriteria === 'nik') return 'No. NIK'
            if (this.searchAnggotaCriteria === 'faktur') return 'No. Faktur'
            return 'Nama Anggota'
        },
        searchCriteriaPlaceholder() {
            if (this.searchAnggotaCriteria === 'nik') return 'Masukkan No. NIK / KTP'
            if (this.searchAnggotaCriteria === 'faktur') return 'Masukkan No. Faktur'
            return 'Masukkan nama anggota'
        },
        totalGadaiBerjalan() {
            if (!this.daftarGadai || !Array.isArray(this.daftarGadai)) return 0
            return this.daftarGadai.filter(item => item.status && item.status.name === 'Berjalan').length
        },
        totalKewajibanBerjalan() {
            if (!this.daftarGadai || !Array.isArray(this.daftarGadai)) return '0'
            const total = this.daftarGadai
                .filter(item => item.status && item.status.name === 'Berjalan')
                .reduce((acc, item) => acc + (Number(item.nilaiPinjamanEfektif) || 0), 0)
            return this.$helper.addThousandSeparator(total)
        }
    },
    methods: {
        setSearchAnggotaCriteria(criteria) {
            this.searchAnggotaCriteria = criteria
            this.searchAnggotaQuery = ''
            this.showAnggotaResultList = false
            this.searchAnggotaResults = []
        },

        handleSearchAnggota() {
            const query = (this.searchAnggotaQuery || '').trim()

            if (this.searchAnggotaCriteria === 'faktur') {
                // Search by No. Faktur
                this.currentSearchMode = 'faktur'
                this.currentPage = 1
                this.getDaftarGadaiFaktur()

                // If a specific no faktur is typed, try to fetch its detail to show member card
                if (query !== '') {
                    this.$axios.get(`gadai/detail/faktur/${encodeURIComponent(query)}`)
                        .then(res => {
                            if (res.data && res.data.anggota) {
                                const a = res.data.anggota
                                this.anggota.id = a.id || a.nasabahId || res.data.nasabahId
                                this.anggota.nama = a.namaAnggota || a.nama || '-'
                                this.anggota.no = a.noAnggota || a.no || '-'
                                this.anggota.jenisIdentitas = a.jenisIdentitas || '-'
                                this.anggota.noIdentitas = a.noIdentitas || '-'
                                this.anggota.noHp = a.noHp || a.noHandphone || '-'
                                this.anggota.noRumah = a.noRumah || '-'
                                this.anggota.alamat = a.alamat || '-'
                                this.showDetailAnggota = true
                            } else {
                                this.showDetailAnggota = false
                            }
                        })
                        .catch(() => {
                            this.showDetailAnggota = false
                        })
                } else {
                    this.showDetailAnggota = false
                }
                return
            }

            // Search by Member Name or NIK
            if (!query) {
                this.$helper.toastErr(this, 'Mohon isi pencarian anggota')
                return
            }

            // searchBy: 1 = NIK / KTP, 2 = Nama Anggota
            const searchBy = this.searchAnggotaCriteria === 'nik' ? 1 : 2
            const queryParam = `?query=${encodeURIComponent(query)}&searchBy=${searchBy}&page=0&size=10`

            this.isSearchingAnggota = true
            this.showAnggotaResultList = false
            this.searchAnggotaResults = []

            this.$axios.get(`nasabah/findForGadai${queryParam}`)
                .then(response => {
                    const data = response.data && response.data.data ? response.data.data : []
                    if (data.length === 0) {
                        this.$helper.toastErr(this, 'Tidak menemukan data anggota dengan kriteria tersebut.')
                    } else if (data.length === 1) {
                        this.selectAnggotaDirect(data[0])
                    } else {
                        this.searchAnggotaResults = data
                        this.showAnggotaResultList = true
                    }
                })
                .catch(error => {
                    this.$helper.parseError(this, error)
                })
                .finally(() => {
                    this.isSearchingAnggota = false
                })
        },

        selectAnggotaDirect(item) {
            this.showAnggotaResultList = false
            this.selectAnggota(item)
        },

        selectAnggota(item) {
            this.anggota.id = item.id
            this.anggota.nama = item.nama
            this.anggota.no = item.no
            this.anggota.jenisIdentitas = item.jenisIdentitas ? (item.jenisIdentitas.name || item.jenisIdentitas) : '-'
            this.anggota.noIdentitas = item.noIdentitas
            this.anggota.noHp = item.noHp
            this.anggota.noRumah = item.noRumah
            this.anggota.alamat = item.alamat
            this.anggota.isComplete = item.isComplete

            this.showDetailAnggota = true
            this.currentSearchMode = 'anggota'
            this.currentPage = 1
            this.getDaftarGadaiAnggota()
        },

        handleResetAnggotaSearch() {
            this.searchAnggotaQuery = ''
            this.showAnggotaResultList = false
            this.searchAnggotaResults = []
            this.showDetailAnggota = false
            this.anggota = {
                id: null,
                nama: '-',
                no: '-',
                jenisBarang: '-',
                jenisIdentitas: '-',
                noIdentitas: '-',
                noHp: '-',
                noRumah: '-',
                alamat: '-',
                isComplete: false
            }
            this.currentSearchMode = 'faktur'
            this.currentPage = 1
            this.getDaftarGadaiFaktur()
        },

        fetchCurrentTableData() {
            if (this.currentSearchMode === 'anggota' && this.anggota && this.anggota.id) {
                this.getDaftarGadaiAnggota()
            } else {
                this.getDaftarGadaiFaktur()
            }
        },

        getDaftarGadaiFaktur() {
            this.daftarGadai = []
            this.isBusy = true
            this.page = this.currentPage - 1
            this.size = this.input.show
            let query = this.$helper.makeQuery(this.page, this.size)
            const filterFaktur = this.searchAnggotaCriteria === 'faktur' ? (this.searchAnggotaQuery || '').trim() : ''
            query += "&noFaktur=" + encodeURIComponent(filterFaktur)

            this.$axios.get(`gadai-pembayaran/search-faktur` + query)
                .then(response => {
                    this.daftarGadai = response.data && response.data.data ? response.data.data : []
                    this.updateTotalItem(response.data ? response.data.total : 0)
                })
                .catch(error => {
                    this.$helper.parseError(this, error)
                })
                .finally(() => {
                    this.isBusy = false
                })
        },

        getDaftarGadaiAnggota() {
            if (!this.anggota || !this.anggota.id) return
            this.daftarGadai = []
            this.isBusy = true
            this.page = this.currentPage - 1
            this.size = this.input.show
            let query = this.$helper.makeQuery(this.page, this.size)

            this.$axios.get(`nasabah/${this.anggota.id}/gadai` + query)
                .then(response => {
                    this.daftarGadai = response.data && response.data.data ? response.data.data : []
                    this.updateTotalItem(response.data ? response.data.total : 0)
                })
                .catch(error => {
                    this.$helper.parseError(this, error)
                })
                .finally(() => {
                    this.isBusy = false
                })
        },

        checkPembayaran(id, noFaktur) {
            let gadaiId = id
            this.$axios.get(`gadai-pembayaran/check-faktur?gadaiId=${gadaiId}`)
                .then(response => {
                    if (response.data == true) {
                        this.$router.push({ path: `${this.$route.path}/${noFaktur}` })
                    }
                })
                .catch(error => {
                    this.$helper.parseError(this, error)
                })
        },

        onLimitChange() {
            this.perPage = this.input.show
            this.currentPage = 1
            this.fetchCurrentTableData()
        },

        onPageChange() {
            if (!this.loading) {
                this.fetchCurrentTableData()
            }
        },

        updateTotalItem(total) {
            this.rows = total || 0
            this.perPage = this.input.show
            let prevLimit = (this.currentPage - 1) * this.input.show + 1
            let limit = (this.currentPage - 1) * this.input.show + this.input.show
            if (limit > this.rows) limit = this.rows
            if (this.rows === 0) {
                this.totalViewText = "Menampilkan 0 dari 0 entri"
            } else {
                this.totalViewText = `Menampilkan ${prevLimit} - ${limit} dari ${this.rows} entri`
            }
        },

        toggleBusy() {
            this.isBusy = !this.isBusy
        }
    },
    mounted() {
        this.currentSearchMode = 'faktur'
        this.fetchCurrentTableData()
    }
}
</script>
